import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import type {
  AddPageManagerMutation,
  AddPageManagerMutationVariables,
  GetPageManagersQuery,
  GetPageManagersQueryVariables,
  GetPageUserContactSettingsQuery,
  GetPageUserContactSettingsQueryVariables,
  RemovePageManagerMutation,
  RemovePageManagerMutationVariables,
  SendShareWithTenantsMutation,
  SendShareWithTenantsMutationVariables,
  UpdatePageManagerMutation,
  UpdatePageManagerMutationVariables,
  UpdatePageUserContactSettingsMutation,
  UpdatePageUserContactSettingsMutationVariables,
} from '@/__generated__/graphql';

export const LIST_OWNED_PAGES = gql`
  query ListOwnedPages {
    ownedPages {
      id
      title
      streetAddress
      city
      postalCode
      country
      slug
      isReportingEnabled
      isChatEnabled
      threeBeeSiteId
      hasBeehives
      hasBioacoustics
    }
  }
`;

export const LIST_BOUNCING_PAGES = gql`
  query ListBouncingContactPages {
    bouncingContactPages {
      id
      title
      slug
      zuperCustomerUid
      zuperHubspotCustomerId
      region
      pageManagers {
        email
        emailAddressStatus
        mainContact
        hiveContact
      }
    }
  }
`;

export const LIST_ALL_ACTIVE_PAGES = gql`
  query ListAllActivePages {
    allActivePages {
      id
      title
      slug
      profilePic
      streetAddress
    }
  }
`;

export const LIST_PAGES_DATA = gql`
  query listPagesData {
    pageData {
      id
      title
      slugs {
        slug
        updatedAt
      }
      myHiveUrl
      profilePic
      streetAddress
      city
      # totalPageUpdateSubscribersCount
      nonManagerPageSubscribersCount
      totalPageUpdateSubscribersCount
      tenantSubCreatedAts
      pageManagers {
        # userId
        role
        # lastSeenAt
        # firstName
        # lastName
        email
        mainContact
        # visitReminders
        # pageId
        # buzzReminders
      }
      managerLastSeenAt
      zuperCustomerUid
      zuperRegion
      zuperCustomerUrl
      zuperCustomerName
      zuperLastSyncedAt
      mainContactEmail
      zuperAlveoleRegion
      zuperPreferredTechnicianName
      numberOfBeeHives
      numberOfBeeHomes
      numberOfBeeHivesInstalled
      numberOfBeeHomesInstalled
      hit25TenantSubscribersAt
      zuperCustomerCategory
      serviceStatus
      lastCompletedBeekeepingVisitDate
      totalCompletedBeekeepingVisitsThisYearCount
      numberOfWorkshopsCompletedThisYear
      numberOfWorkshopsBookedThisYear
      workshopsAllotted
      createdAt
      assets {
        id
        hiveHealth
        createdAt
        updatedAt
        pageId
        assetCategory
        assetStatus
      }
    }
  }
`;

export const GET_PAGE_OUTBOUND_LOGS = gql`
  query GetPageOutboundLogs($pageId: String!) {
    pageOutboundLogs(pageId: $pageId) {
      updateUuid
      updateContext
      logGroup
      sentAt
      medium
      recipients {
        recipient
        firstName
        lastName
        twilioStatus
        cioStatus
      }
    }
  }
`;

export const GET_PAGE_HOME = gql`
  query GetPageHome($slug: String!) {
    page(slug: $slug) {
      id
      title
      description
      threeBeeSiteId
      language
      searchVisibility
      slug
      country
      profilePic
      profilePicOriginal
      coverPic
      coverPicOriginal
      companyLogo
      isReportingEnabled
      chatBotEnabled
      intercomChatBotEnabled
      totalPageUpdateSubscribers
      hasRecentPageUpdateSubscribers
      honeyDeliveredBy
      honeyNextHarvest
      zuperCustomerUid
      lat
      lng
      address
      events {
        id
        timezone
        startTime
        endTime
        eventType
        beekeeperFirstName
        beekeeperLastName
        registeredCount
        registeredUsers {
          id
          email
          firstName
          lastName
          organizationName
          registrationStatus
        }
        title {
          en
          fr
        }
        description {
          en
          fr
        }
        capacity
        imageFilename
        zuperJobUid
      }
      beekeepingVisits {
        id
        timezone
        title {
          en
          fr
        }
        startTime
        endTime
        beekeeperFirstName
        beekeeperLastName
      }
      buzzes {
        id
        authorId
        beekeeperFirstName
        beekeeperLastName
        beekeeperTagline
        beekeeperAvatarUrl
        likedByMe
        totalLikes
        text
        media {
          id
          fileUrl
          mimeType
          buzzId
        }
        weatherDescription
        weatherTempCelsius
        city
        createdAt
        isYearEnd
      }
      yearEndBuzz {
        id
        text
        likedByMe
        totalLikes
        media {
          id
          fileUrl
          mimeType
        }
        createdAt
      }
      eventWaitingList {
        id
        email
        firstName
        lastName
        organizationName
      }
      honeyWaitingList {
        id
        email
        firstName
        lastName
        organizationName
      }
      pageSubscriberEmails
      eventInterestEnabled
      honeyInterestEnabled
      eventRegistrationEnabled
      showUpcomingEvents
      showUpcomingVisits
      queenFeatureEnabled
      queenCommunityVotingEnabled
      eventHighlightCard {
        buzzId
        title {
          en
          fr
        }
        buzzText
        mediaPath
        eventDate
        buzzId
      }
      zuperCustomerTimezone
      zuperAlveoleRegion
      address
      numberOfWorkshopsBookedThisYear
      contractedWorkshops
      hasBioacoustics
    }
  }
`;

export const GET_PAGE_MEDIA = gql`
  query GetPageMedia($slug: String!) {
    pageMedia(slug: $slug) {
      id
      fileUrl
      mimeType
      buzzId
    }
  }
`;

export const ADD_PAGE_SUBSCRIBER = gql`
  mutation AddPageSubscriber($AddToWaitingListInput: AddToWaitingListInput!) {
    addPageSubscriber(input: $AddToWaitingListInput)
  }
`;

export const ADD_TO_EVENT_WAITING_LIST = gql`
  mutation AddToEventWaitingList($AddToWaitingListInput: AddToWaitingListInput!) {
    addToEventWaitingList(input: $AddToWaitingListInput)
  }
`;

export const ADD_TO_HONEY_WAITING_LIST = gql`
  mutation AddToHoneyWaitingList($AddToWaitingListInput: AddToWaitingListInput!) {
    addToHoneyWaitingList(input: $AddToWaitingListInput)
  }
`;

export const UPDATE_PAGE_PREFERENCES = gql`
  mutation UpdatePagePreferences(
    $pageId: String!
    $title: String!
    $description: String!
    $language: Language!
    $searchVisibility: SearchVisibility!
    $slug: String!
    $eventInterestEnabled: Boolean!
    $honeyInterestEnabled: Boolean!
    $eventRegistrationEnabled: Boolean!
    $intercomChatBotEnabled: Boolean!
    $showUpcomingEvents: Boolean!
    $showUpcomingVisits: Boolean!
    $queenFeatureEnabled: Boolean!
    $queenCommunityVotingEnabled: Boolean!
  ) {
    updatePagePreferences(
      pageId: $pageId
      title: $title
      description: $description
      language: $language
      searchVisibility: $searchVisibility
      slug: $slug
      eventInterestEnabled: $eventInterestEnabled
      honeyInterestEnabled: $honeyInterestEnabled
      eventRegistrationEnabled: $eventRegistrationEnabled
      intercomChatBotEnabled: $intercomChatBotEnabled
      showUpcomingEvents: $showUpcomingEvents
      showUpcomingVisits: $showUpcomingVisits
      queenFeatureEnabled: $queenFeatureEnabled
      queenCommunityVotingEnabled: $queenCommunityVotingEnabled
    )
  }
`;

export const UPDATE_PAGE_MANAGER: TypedDocumentNode<UpdatePageManagerMutation, UpdatePageManagerMutationVariables> =
  gql`
    mutation UpdatePageManager($input: UpdatePageManagerInput!) {
      updatePageManager(updatePageManagerInput: $input)
    }
  `;

export const RESEND_INVITE_EMAIL = gql`
  mutation ResendInviteEmail($email: String!, $pageId: String!) {
    resendInviteEmail(email: $email, pageId: $pageId)
  }
`;

export const REMOVE_PAGE_MANAGER: TypedDocumentNode<RemovePageManagerMutation, RemovePageManagerMutationVariables> =
  gql`
    mutation RemovePageManager($userId: String!, $pageId: String!) {
      removePageManager(userId: $userId, pageId: $pageId)
    }
  `;

export const GET_PAGE_MANAGERS: TypedDocumentNode<GetPageManagersQuery, GetPageManagersQueryVariables> = gql`
  query GetPageManagers($slug: String!) {
    pageManagers(slug: $slug) {
      pageId
      userId
      role
      firstName
      lastName
      jobTitle
      phone
      mobilePhone
      email
      visitReminders
      buzzReminders
      subscriberAlertsOptIn
      mainContact
      hiveContact
      contactRole
      emailAddressStatus
      phoneNumberVerification {
        status
        smsOptIn
        twilioStatus
        twilioErrorCode
        twilioErrorMessage
        lastMessageSentAt
      }
      lastSeenAt
      language
    }
  }
`;

export const GET_PAGE_USER_CONTACT_SETTINGS: TypedDocumentNode<
  GetPageUserContactSettingsQuery,
  GetPageUserContactSettingsQueryVariables
> = gql`
  query GetPageUserContactSettings($pageId: String!, $email: String!, $token: String) {
    pageUserContactSettings(pageId: $pageId, email: $email, token: $token) {
      mainContact
      hiveContact
      visitReminders
      buzzReminders
      subscriberAlertsOptIn
      eventWaitingList
      emailAddressStatus
      isPageManager
      page {
        pageId
        slug
        title
      }
    }
  }
`;

export const UPDATE_PAGE_USER_CONTACT_SETTINGS: TypedDocumentNode<
  UpdatePageUserContactSettingsMutation,
  UpdatePageUserContactSettingsMutationVariables
> = gql`
  mutation UpdatePageUserContactSettings($input: UpdatePageUserContactSettingsInput!, $token: String) {
    updatePageUserContactSettings(input: $input, token: $token) {
      mainContact
      hiveContact
      visitReminders
      buzzReminders
      subscriberAlertsOptIn
      eventWaitingList
      emailAddressStatus
    }
  }
`;

export const ADD_PAGE_MANAGER: TypedDocumentNode<AddPageManagerMutation, AddPageManagerMutationVariables> = gql`
  mutation AddPageManager($input: AddPageManagerInput!) {
    addPageManager(addPageManagerInput: $input)
  }
`;

export const POST_BANNER_IMAGE = gql`
  mutation UpdateBannerImage($input: UpdateBannerImageInput!) {
    updateBannerImage(updateBannerImageInput: $input)
  }
`;

export const POST_PROFILE_IMAGE = gql`
  mutation UpdateProfileImage($input: UpdateProfileImageInput!) {
    updateProfileImage(updateProfileImageInput: $input)
  }
`;

export const UPDATE_COMPANY_LOGO = gql`
  mutation UpdateCompanyLogo($input: UpdateCompanyLogoInput!) {
    updateCompanyLogo(updateCompanyLogoInput: $input)
  }
`;

export const GET_PAGE_HONEY_LABEL = gql`
  query GetPageHoneyLabel($slug: String!) {
    getPageHoneyLabel(slug: $slug) {
      zuperJobId
      fileUrl
      status
      designRefId
    }
  }
`;

export const UPDATE_HONEY_LABEL_STATUS = gql`
  mutation UpdateHoneyLabelStatus($input: HoneyLabelStatusInput!) {
    updateHoneyLabelStatus(honeyLabelStatusInput: $input)
  }
`;

export const SEND_SHARE_WITH_TENANTS: TypedDocumentNode<
  SendShareWithTenantsMutation,
  SendShareWithTenantsMutationVariables
> = gql`
  mutation SendShareWithTenants($input: ShareWithTenantsInput!) {
    sendShareWithTenants(input: $input)
  }
`;

export const GET_THREEBEE_IDS = gql`
  query GetThreeBeeIds($siteId: Int!) {
    getThreeBeeSiteIds(siteId: $siteId) {
      siteId
      polygonId
      caId
      roiId
      portfolioId
    }
  }
`;

export const LIST_OWNED_PAGES_DASHBOARD = gql`
  query ListOwnedPagesDashboard {
    ownedPagesDashboard {
      id
      title
      streetAddress
      country
      lat
      lng
      slug
      zuperCustomerUid
      region
      profilePic
      tenantSubCount
      numberOfWorkshopsBookedThisYear
      contractedWorkshops
      threeBeeSiteId
      pageManagerName
      package
      newSubs
      yearEndDownloadUrl
      pesticideDownloadUrl
      events {
        id
        startTime
        endTime
        timezone
        eventType
        registeredCount
        title {
          en
          fr
        }
      }

      beekeepingVisits {
        id
        startTime
        endTime
        timezone
      }

      installations {
        id
        startTime
        endTime
        timezone
      }

      honeyJars {
        id
        dueDate
        status
        timezone
      }

      honeyHarvests {
        id
        dueDate
        status
        timezone
      }

      latestBuzzes {
        id
        text
        createdAt
        timezone
        latestImageMedia {
          fileUrl
          mimeType
        }
      }
    }
  }
`;

export const PAGE_GROUPS_QUERY = gql`
  query PageGroups {
    pageGroups {
      id
      name
      description
      pageIds
    }
  }
`;

export const CREATE_PAGE_GROUP = gql`
  mutation CreatePageGroup($input: CreatePageGroupInput!) {
    createPageGroup(input: $input) {
      id
      name
      description
      pageIds
    }
  }
`;

export const UPDATE_PAGE_GROUP = gql`
  mutation UpdatePageGroup($input: UpdatePageGroupInput!) {
    updatePageGroup(input: $input) {
      id
      name
      description
      pageIds
    }
  }
`;

export const DELETE_PAGE_GROUP = gql`
  mutation DeletePageGroup($id: String!) {
    deletePageGroup(id: $id)
  }
`;

export const ADD_PAGES_TO_GROUP = gql`
  mutation AddPagesToGroup($input: AddPagesToGroupInput!) {
    addPagesToGroup(input: $input)
  }
`;

export const REMOVE_PAGE_FROM_GROUP = gql`
  mutation RemovePageFromGroup($input: RemovePageFromGroupInput!) {
    removePageFromGroup(input: $input)
  }
`;

export const GET_PAGES_IN_GROUP = gql`
  query PagesInGroup($pageGroupId: String!) {
    pagesInGroup(pageGroupId: $pageGroupId)
  }
`;

export const GROUPS_FOR_PAGE = gql`
  query GroupsForPage($pageId: String!) {
    groupsForPage(pageId: $pageId) {
      id
      name
      description
    }
  }
`;

export const ADD_USER_TO_ALL_GROUP_PAGES = gql`
  mutation AddUserToAllGroupPages($input: AddUserToGroupPagesInput!) {
    addUserToAllGroupPages(input: $input)
  }
`;

export const REMOVE_USER_FROM_ALL_GROUP_PAGES = gql`
  mutation RemoveUserFromAllGroupPages($input: AddUserToGroupPagesInput!) {
    removeUserFromAllGroupPages(input: $input)
  }
`;

export const ADD_USER_TO_ALL_USER_TO_CLONE_PAGES = gql`
  mutation AddUserToAllUserToClonePages($input: AddUserToUserToClonePagesInput!) {
    addUserToAllUserToClonePages(input: $input)
  }
`;

export const REMOVE_USER_FROM_ALL_USER_TO_CLONE_PAGES = gql`
  mutation RemoveUserFromAllUserToClonePages($input: AddUserToUserToClonePagesInput!) {
    removeUserFromAllUserToClonePages(input: $input)
  }
`;

export const CLEAR_ALL_USER_PAGES = gql`
  mutation ClearAllUserPages($userId: String!) {
    clearAllUserPages(userId: $userId)
  }
`;
export const GET_PAGE_GROUPS_IN_USER_OWNED_PAGES = gql`
  query getPageGroupsInUserOwnedPages {
    pageGroupsInUserOwnedPages {
      id
      name
      description
      pageIds
    }
  }
`;

export const GET_CLONED_USERS_IN_UESR_OWNED_PAGES = gql`
  query getClonedUsersInUserOwnedPages($userId: String!) {
    clonedUsersInUserOwnedPages(userId: $userId) {
      id
      firstName
      lastName
      email
    }
  }
`;

export const GET_KIOSK_FROM_ZUPER_JOB_UID = gql`
  query GetKioskFromZuperJobUid($zuperJobUid: String!) {
    getKioskFromZuperJobUid(zuperJobUid: $zuperJobUid) {
      pageId
      title
      description
      coverPic
      profilePic
      slug
      eventId
      eventType
      eventTitle {
        en
        fr
      }
      eventDescription {
        en
        fr
      }
      eventImageFilename
      language
    }
  }
`;

export const REGISTER_KIOSK_ATTENDEES = gql`
  mutation RegisterKioskAttendees($input: RegisterKioskAttendeesInput!) {
    registerKioskAttendees(input: $input)
  }
`;

export const GET_PAGE_AUDIENCE_SUMMARY = gql`
  query GetPageAudienceSummary($pageId: String!) {
    pageAudienceSummary(pageId: $pageId) {
      subscribersCount
      honeyInterestCount
      registeredForAnEventCount
      eventInterestCount
      users {
        firstName
        lastName
        email
        organizationName
        isSubscriber
        interestedInHoney
        interestedInEvents
        eventStatuses {
          eventId
          eventTitle
          eventStartTime
          registrationStatus
        }
      }
    }
  }
`;

export const SEND_SHARE_WITH_COLLEAGUE = gql`
  mutation sendShareWithColleague($input: ShareWithColleagueInput!) {
    sendShareWithColleague(input: $input)
  }
`;

export const REGIONS_QUERY = gql`
  query Regions {
    regions {
      id
      name
      territoryLeadUserId
      territoryLead {
        id
        firstName
        lastName
        email
      }
    }
  }
`;

export const STAFF_USERS_QUERY = gql`
  query StaffUsers {
    staffUsers {
      id
      firstName
      lastName
      email
      role
    }
  }
`;

export const CREATE_REGION = gql`
  mutation CreateRegion($input: CreateRegionInput!) {
    createRegion(input: $input) {
      id
      name
      territoryLeadUserId
      territoryLead {
        id
        firstName
        lastName
        email
      }
    }
  }
`;

export const UPDATE_REGION = gql`
  mutation UpdateRegion($input: UpdateRegionInput!) {
    updateRegion(input: $input) {
      id
      name
      territoryLeadUserId
      territoryLead {
        id
        firstName
        lastName
        email
      }
    }
  }
`;

export const DELETE_REGION = gql`
  mutation DeleteRegion($id: String!) {
    deleteRegion(id: $id)
  }
`;
